import {
  isSentinel,
  sentinelLabel,
  formatCurrency,
  formatMultiplier,
  enumLabel,
  formatMultipleDollarAmounts,
  formatMultipleSalaryMultipliers,
  ANNUAL_OPEN_ENROLLMENT_LABELS,
  INCLUSION_LABELS,
  formatRateGuarantee,
} from "./shared";

const COVERAGE_TYPE_LABELS: Record<string, string> = {
  faceValue: "Face Value",
  cashValue: "Cash Value",
  moneyPurchase: "Money Purchase",
};

/**
 * Employee Benefit Amount — increments/dollarAmount → $X;
 * salaryMultiplier → Nx salary; the multi-value types → their comma-separated
 * values.
 */
const formatEmployeeBenefitAmount = (row: Record<string, unknown>): string => {
  const type = row["employeeBenefitAmountType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "dollarAmount" || type === "increments")
    return formatCurrency(row["employeeBenefitAmountAmount"]) || "";
  if (type === "salaryMultiplier")
    return formatMultiplier(row["employeeBenefitAmountAmount"]) || "";
  if (type === "multipleSalaryMultipliers")
    return formatMultipleSalaryMultipliers(row["employeeBenefitAmountAmount"]);
  if (type === "multipleOptions" || type === "multipleDollarAmounts")
    return formatMultipleDollarAmounts(row["employeeBenefitAmountAmount"]);
  return "";
};

/**
 * Employee Benefit Maximum — dollarAmount/salaryMultiplier/lesserOf.
 * Siblings: employeeBenefitMaximumDollarAmount, employeeBenefitMaximumSalaryMultiplier.
 */
const formatEmployeeBenefitMaximum = (row: Record<string, unknown>): string => {
  const type = row["employeeBenefitMaximumType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  if (type === "dollarAmount")
    return formatCurrency(row["employeeBenefitMaximumDollarAmount"]) || "";
  if (type === "salaryMultiplier")
    return (
      formatMultiplier(row["employeeBenefitMaximumSalaryMultiplier"]) || ""
    );
  if (type === "lesserOfDollarAmountOrSalaryMultiplier") {
    const dollar = formatCurrency(row["employeeBenefitMaximumDollarAmount"]);
    const mult = formatMultiplier(
      row["employeeBenefitMaximumSalaryMultiplier"],
    );
    if (dollar && mult) return `Lesser of ${dollar} or ${mult}`;
    return dollar || mult || "";
  }
  return "";
};

/** Riders Included / Additional Options Included — free text, passed through as-is. */
const freeText =
  (fieldKey: string) =>
  (row: Record<string, unknown>): string => {
    const value = row[fieldKey];
    return typeof value === "string" ? value : "";
  };

/** Spouse/Child benefit (amount or maximum) — dollarAmount / notIncluded. */
const dependentBenefit =
  (typeKey: string, amountKey: string) =>
  (row: Record<string, unknown>): string => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);

    if (type === "dollarAmount") return formatCurrency(row[amountKey]) || "";
    return "";
  };

const COVERAGE_EFFECTIVE_DATE_LABELS: Record<string, string> = {
  firstOfTheMonthFollowingEffectiveDate:
    "1st of Month Following Effective Date",
  firstOfTheMonthFollowingSignedApplication:
    "1st of Month Following Signed Application",
  firstDayOfTheMonthInWhichPayrollDeductionsBegin:
    "1st of Month Payroll Deductions Begin",
  coverageIsEffectiveOnThePolicyDate: "Policy Date",
  dateOfSignedApplication: "Date of Signed Application",
  notIncluded: "Not Included",
};

export const wholeLifeFormatters: Record<
  string,
  (row: Record<string, unknown>) => string
> = {
  coverageType: enumLabel("coverageType", COVERAGE_TYPE_LABELS),
  employeeBenefitAmount: formatEmployeeBenefitAmount,
  employeeBenefitMaximum: formatEmployeeBenefitMaximum,
  guaranteeIssueAmount: (row) =>
    formatCurrency(row["guaranteeIssueAmount"]) || "",
  ridersIncluded: freeText("ridersIncluded"),
  additionalOptionsIncluded: freeText("additionalOptionsIncluded"),
  spouseBenefitAmount: dependentBenefit(
    "spouseBenefitAmountType",
    "spouseBenefitAmountAmount",
  ),
  spouseBenefitMaximum: dependentBenefit(
    "spouseBenefitMaximumType",
    "spouseBenefitMaximumAmount",
  ),
  childBenefitAmount: dependentBenefit(
    "childBenefitAmountType",
    "childBenefitAmountAmount",
  ),
  childBenefitMaximum: dependentBenefit(
    "childBenefitMaximumType",
    "childBenefitMaximumAmount",
  ),
  interestRate: (row) => {
    const num = Number(row["interestRate"]);
    return Number.isFinite(num) ? `${num}%` : "";
  },
  endowsAge: (row) => {
    const age = row["endowsAge"];
    return age != null ? `Age ${String(age)}` : "";
  },
  coverageEffectiveDate: enumLabel(
    "coverageEffectiveDate",
    COVERAGE_EFFECTIVE_DATE_LABELS,
  ),
  annualOpenEnrollment: enumLabel(
    "annualOpenEnrollment",
    ANNUAL_OPEN_ENROLLMENT_LABELS,
  ),
  portability: enumLabel("portability", INCLUSION_LABELS),
  rateGuarantee: formatRateGuarantee,
};

export const formatWholeLifeField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = wholeLifeFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
